import React, { useState, useEffect, useCallback } from 'react';
import { Map } from 'lucide-react';
import { toast } from 'react-toastify';
import { seoAPI } from '../../../config/seo-api';
import { helpers } from '../../../utils/helpers';
import { filterCategoriesByAccess } from '../../../utils/categoryAccessFilter';
import {
  getProductSeoScore,
  getProductSeoStatus,
  getSeoStatusClass,
  SEO_SCORE_FILTER_OPTIONS,
  getPrimaryProductCategory,
} from '../../../utils/seoHelpers';
import EditSeoModal from './EditSeoModal';
import '../ProductList/ProductList.css';
import './Seo.css';

const PAGE_LIMIT = 50;

const getCategoryDisplay = (product) => {
  const category = getPrimaryProductCategory(product);
  return category || '-';
};

const getAvailabilityStatus = (isAvailable) => (isAvailable ? 'In Stock' : 'Out of Stock');

const getAvailabilityClass = (isAvailable) =>
  isAvailable ? 'status-available' : 'status-unavailable';

const SeoProductList = ({ adminData, embedded = false, onLoadLastSitemapCreated }) => {
  const hasAccess = helpers.hasSeoAccess(adminData);

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [accessDeniedMessage, setAccessDeniedMessage] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [availableCount, setAvailableCount] = useState(0);
  const [notAvailableCount, setNotAvailableCount] = useState(0);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [categoriesData, setCategoriesData] = useState([]);
  const [generalSubCategories, setGeneralSubCategories] = useState([]);
  const [filters, setFilters] = useState({
    status: 'all',
    category: 'all',
    subCategory: 'all',
    imageFilter: 'all',
    seoScoreFilter: 'all',
    searchQuery: '',
  });
  const [pageInput, setPageInput] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [creatingSitemap, setCreatingSitemap] = useState(false);
  const [lastSitemapCreatedAt, setLastSitemapCreatedAt] = useState(null);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_LIMIT));

  const fetchProducts = useCallback(async (
    page = 1,
    search = '',
    filterStatus = 'all',
    filterCategory = 'all',
    filterSubCategory = 'all',
    filterImage = 'all',
    filterSeoScore = 'all'
  ) => {
    if (!hasAccess) {
      setAccessDenied(true);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setAccessDenied(false);
      setAccessDeniedMessage('');

      const response = await seoAPI.fetchSeoProducts({
        page,
        limit: PAGE_LIMIT,
        searchQuery: search,
        filterStatus,
        filterCategory,
        filterSubCategory,
        filterImage,
        seoScoreFilter: filterSeoScore,
      });

      if (response.success) {
        setProducts(response.data?.products || []);
        setTotalCount(response.data?.totalCount || 0);
        setAvailableCount(response.data?.availableCount || 0);
        setNotAvailableCount(response.data?.notAvailableCount || 0);
        setLastSitemapCreatedAt(response.data?.lastSitemapCreatedAt || null);
        if (onLoadLastSitemapCreated) {
          onLoadLastSitemapCreated(response.data?.lastSitemapCreatedAt || null);
        }

        if (response.data.categories && response.data.categories.length > 0) {
          const transformedCategories = response.data.categories.map((cat) => ({
            id: cat._id,
            title: cat.title,
            subcategories: cat.subCategories
              ? cat.subCategories.map((sub) => ({
                  id: sub._id || sub,
                  title: sub.title || sub,
                }))
              : [],
          }));

          const filtered = filterCategoriesByAccess(transformedCategories);
          const categoriesForComponent = filtered.map((cat) => ({
            title: cat.title,
            subCategories: cat.subcategories ? cat.subcategories.map((sub) => sub.title) : [],
          }));

          setCategoriesData(categoriesForComponent);
          setCategories(categoriesForComponent.map((cat) => cat.title));
        }
      } else {
        const message = response.msg || response.error || response.message || 'Failed to fetch SEO products';
        if (
          response.status === 403
          || message.toLowerCase().includes('permission')
          || message.toLowerCase().includes('access')
        ) {
          setAccessDenied(true);
          setAccessDeniedMessage(message);
        } else {
          setError(message);
        }
      }
    } catch (err) {
      console.error('SEO list fetch error:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [hasAccess]);

  const fetchGeneralSubCategories = async () => {
    try {
      const { categoryAPI } = await import('../../../config/category-api');
      const response = await categoryAPI.getGeneralSubcategories();
      if (response.success && response.data) {
        setGeneralSubCategories(response.data.map((g) => ({ ...g, id: g._id })));
      }
    } catch (fetchError) {
      console.error('Error fetching general subcategories:', fetchError);
    }
  };

  useEffect(() => {
    fetchGeneralSubCategories();
  }, []);

  useEffect(() => {
    fetchProducts(
      currentPage,
      filters.searchQuery,
      filters.status,
      filters.category,
      filters.subCategory,
      filters.imageFilter,
      filters.seoScoreFilter
    );
  }, [currentPage, fetchProducts]);

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => {
      const newFilters = { ...prev, [filterType]: value };

      if (filterType === 'category') {
        newFilters.subCategory = 'all';

        if (value === 'all' || value === 'uncategorized') {
          setSubCategories([]);
        } else {
          const selectedCategory = categoriesData.find((cat) => cat.title === value);
          if (selectedCategory?.subCategories) {
            setSubCategories(['none', ...selectedCategory.subCategories]);
          } else {
            setSubCategories(['none']);
          }
        }
      }

      return newFilters;
    });
  };

  const applyFilters = () => {
    setCurrentPage(1);
    fetchProducts(
      1,
      filters.searchQuery,
      filters.status,
      filters.category,
      filters.subCategory,
      filters.imageFilter,
      filters.seoScoreFilter
    );
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    setPageInput('');
  };

  const handleGoButton = () => {
    const page = parseInt(pageInput, 10);
    if (!isNaN(page) && page >= 1 && page <= totalPages) {
      handlePageChange(page);
    }
  };

  const handleEditSeo = (product) => {
    setSelectedProduct(product);
    setShowEditModal(true);
  };

  const handleCloseModal = () => {
    setShowEditModal(false);
    setSelectedProduct(null);
  };

  const handleSeoSaved = (updatedProduct) => {
    setProducts((prev) =>
      prev.map((p) => (p._id === updatedProduct._id ? { ...p, ...updatedProduct } : p))
    );
  };

  const handleCreateSitemap = async () => {
    try {
      setCreatingSitemap(true);
      const response = await seoAPI.createSitemap();

      if (response.success) {
        if (response.data?.lastSitemapCreatedAt) {
          setLastSitemapCreatedAt(response.data.lastSitemapCreatedAt);
        }
        const publicUrl = response.data?.publicUrl;
        const successMessage = publicUrl
          ? `${response.msg || 'Successfully created sitemap'} Public URL: ${publicUrl}`
          : (response.msg || 'Successfully created sitemap');

        if (response.downloaded) {
          toast.success(`${successMessage} Sitemap downloaded.`);
        } else {
          toast.warn(
            `${successMessage} File was created on the server, but automatic download failed.${
              publicUrl ? ` Open: ${publicUrl}` : ''
            }`
          );
        }
      } else {
        toast.error(response.msg || 'Failed to create sitemap');
      }
    } catch (error) {
      toast.error('Failed to create sitemap');
      console.error('Create sitemap error:', error);
    } finally {
      setCreatingSitemap(false);
    }
  };

  if (!hasAccess) {
    return (
      <div className="product-list-page">
        <div className="seo-no-access">
          <h2>Access Denied</h2>
          <p>You do not have permission to view the SEO section.</p>
        </div>
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="product-list-page">
        <div className="seo-no-access">
          <h2>Access Denied</h2>
          <p>{accessDeniedMessage || 'The server rejected your SEO access request.'}</p>
          <button
            type="button"
            className="retry-button"
            onClick={() =>
              fetchProducts(
                currentPage,
                filters.searchQuery,
                filters.status,
                filters.category,
                filters.subCategory,
                filters.imageFilter,
                filters.seoScoreFilter
              )
            }
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const listContent = (
    <>
      {!embedded && (
        <div className="page-header">
          <div className="page-title">
            <h1>SEO</h1>
            <p>Manage product search engine optimization settings</p>
          </div>
          <div className="sitemap-control-container">
          <button
              type="button"
              className="seo-create-sitemap-button"
              onClick={handleCreateSitemap}
              disabled={creatingSitemap}
            >
              <Map size={18} />
              {creatingSitemap ? 'Creating...' : 'Create Sitemap'}
            </button>
            {lastSitemapCreatedAt && (
            <div className="last-sitemap-date">
              Last Created: {lastSitemapCreatedAt}
            </div>
          )}
        </div>
      </div>
      )}

      <div className="filter-section">
        <div className="filter-form">
          <div className="filter-group">
            <label>Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="filter-select"
            >
              <option value="all">All</option>
              <option value="instock">In Stock</option>
              <option value="outofstock">Out of Stock</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Category</label>
            <select
              value={filters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
              className="filter-select"
            >
              <option value="all">All</option>
              {(!adminData?.categoryAccess
                || adminData.categoryAccess === 'all'
                || (Array.isArray(adminData.categoryAccess)
                  && adminData.categoryAccess.some((item) => item.category === 'uncategorized'))) && (
                <option value="uncategorized">Uncategorized</option>
              )}
              {generalSubCategories.map((gen) => (
                <option key={gen._id} value={gen.title}>
                  {gen.title} (G)
                </option>
              ))}
              {categories.map((category) => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Sub Category</label>
            <select
              value={filters.subCategory}
              onChange={(e) => handleFilterChange('subCategory', e.target.value)}
              className="filter-select"
              disabled={
                filters.category === 'all'
                || subCategories.length === 0
                || generalSubCategories.some((gen) => gen.title === filters.category)
              }
            >
              <option value="all">All</option>
              <option value="none">None</option>
              {subCategories.filter((sub) => sub !== 'none').map((subCategory) => (
                <option key={subCategory} value={subCategory}>
                  {subCategory}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Image</label>
            <select
              value={filters.imageFilter}
              onChange={(e) => handleFilterChange('imageFilter', e.target.value)}
              className="filter-select"
            >
              <option value="all">All</option>
              <option value="have_image">Have Image</option>
              <option value="no_image">No Image</option>
            </select>
          </div>

          <div className="filter-group">
            <label>SEO Score</label>
            <select
              value={filters.seoScoreFilter}
              onChange={(e) => handleFilterChange('seoScoreFilter', e.target.value)}
              className="filter-select"
            >
              {SEO_SCORE_FILTER_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Search</label>
            <input
              type="text"
              placeholder="Search by name or product Id..."
              value={filters.searchQuery}
              onChange={(e) => handleFilterChange('searchQuery', e.target.value)}
              className="filter-input"
            />
          </div>

          <button type="button" onClick={applyFilters} className="apply-filters-button">
            Apply Filters
          </button>
        </div>
      </div>

      <div className="stats-section">
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-number stat-no-color">{totalCount.toLocaleString()}</div>
            <div className="stat-label">Total Products</div>
          </div>
          <div className="stat-card available">
            <div className="stat-number">{availableCount.toLocaleString()}</div>
            <div className="stat-label">In Stock</div>
          </div>
          <div className="stat-card unavailable">
            <div className="stat-number">{notAvailableCount.toLocaleString()}</div>
            <div className="stat-label">Out of Stock</div>
          </div>
        </div>
      </div>

      <div className="products-table-container">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner" />
            <p>Loading products...</p>
          </div>
        ) : error ? (
          <div className="error-state">
            <p>{error}</p>
            <button
              type="button"
              onClick={() =>
                fetchProducts(
                  currentPage,
                  filters.searchQuery,
                  filters.status,
                  filters.category,
                  filters.subCategory,
                  filters.imageFilter,
                  filters.seoScoreFilter
                )
              }
              className="retry-button"
            >
              Retry
            </button>
          </div>
        ) : products.length === 0 ? (
          <div className="empty-state">
            <p>No products found</p>
          </div>
        ) : (
          <>
            <div className="table-wrapper">
              <table className="products-table">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Id</th>
                    <th>Image</th>
                    <th>Barcode</th>
                    <th>Title</th>
                    <th>Category</th>
                    <th>SEO Status</th>
                    <th>SEO Score</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product) => {
                    const score = getProductSeoScore(product);
                    const seoStatus = getProductSeoStatus(product);
                    const statusClass = getSeoStatusClass(seoStatus, product.seoStatusBadgeClass);

                    return (
                      <tr key={product._id}>
                        <td>
                          <span className={`status ${getAvailabilityClass(product.isAvailable)}`}>
                            {getAvailabilityStatus(product.isAvailable)}
                          </span>
                        </td>
                        <td>
                          <span className="product-id-link disabled">{product.productId}</span>
                        </td>
                        <td className="table-product-image">
                          {product.images && product.images.length > 0 ? (
                            <span className="image-checkmark">✅</span>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td>{product.barcode || '-'}</td>
                        <td className="product-name">
                          <span>{product.name}</span>
                        </td>
                        <td>{getCategoryDisplay(product)}</td>
                        <td>
                          <span className={`seo-status-badge ${statusClass}`}>{seoStatus}</span>
                        </td>
                        <td>
                          <div className="seo-score-cell">
                            <span className="seo-score-text">{score}/100</span>
                            <div className="seo-score-bar">
                              <div
                                className="seo-score-fill"
                                style={{ width: `${Math.min(100, Math.max(0, score))}%` }}
                              />
                            </div>
                          </div>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="seo-edit-button"
                            onClick={() => handleEditSeo(product)}
                          >
                            Edit SEO
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="pagination">
              <div className="page-input-container">
                <input
                  type="number"
                  value={pageInput}
                  min="1"
                  max={totalPages}
                  placeholder="Page"
                  className="page-input"
                  onChange={(e) => setPageInput(e.target.value)}
                />
                <button type="button" onClick={handleGoButton} className="go-button">
                  Go
                </button>
              </div>

              <button
                type="button"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="pagination-button"
              >
                Previous
              </button>
              <span className="pagination-info">
                Page {currentPage} of {totalPages}
              </span>
              <button
                type="button"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= totalPages}
                className="pagination-button"
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>

      {showEditModal && selectedProduct && (
        <EditSeoModal
          product={selectedProduct}
          adminData={adminData}
          onClose={handleCloseModal}
          onSaved={handleSeoSaved}
        />
      )}
    </>
  );

  if (embedded) {
    return listContent;
  }

  return <div className="product-list-page">{listContent}</div>;
};

export default SeoProductList;
